// ✅ AdminAuditAnalytics (FIXED)
// Fixes your 403 / "jwt malformed" by:
// 1) Using the same token approach as your working pages (reads from localStorage if prop missing)
// 2) Normalizing token (removes "Bearer ", quotes, JSON-string mistakes)
// 3) Decoding JWT AFTER normalization for campus default
// Theme kept exactly like your “above theme”

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { API_URL } from "./config";

// --- normalize token so we never send Bearer Bearer / quotes / JSON ---
function normalizeToken(t) {
  if (!t) return "";
  let s = String(t).trim();

  // if token stored as JSON string like "\"eyJ...\""
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }

  // if token was mistakenly stored as JSON object string: {"token":"..."}
  // (safe: only parse if it looks like JSON)
  if (s.startsWith("{") && s.endsWith("}")) {
    try {
      const obj = JSON.parse(s);
      if (obj?.token) s = String(obj.token).trim();
      else if (obj?.auth) s = String(obj.auth).trim();
    } catch {
      // ignore
    }
  }

  // strip Bearer prefix if present
  if (/^bearer\s+/i.test(s)) s = s.replace(/^bearer\s+/i, "").trim();

  // strip again if it became "Bearer Bearer ..."
  if (/^bearer\s+/i.test(s)) s = s.replace(/^bearer\s+/i, "").trim();

  return s;
}

// --- simple JWT payload decode (no signature verify; just for reading claims) ---
function decodeJwtPayloadFromRawJwt(rawJwt) {
  try {
    if (!rawJwt) return null;
    const parts = rawJwt.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export default function AdminAuditAnalytics({ token: tokenProp }) {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, bgcolor: BG, minHeight: "100vh" };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = { ...surfaceSx, mt: 1, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ✅ IMPORTANT: match your working pages — read token from localStorage if prop not provided
  const tokenFromStorage = typeof window !== "undefined" ? localStorage.getItem("auth") : "";
  const rawToken = useMemo(
    () => normalizeToken(tokenProp || tokenFromStorage),
    [tokenProp, tokenFromStorage]
  );

  const [tab, setTab] = useState(0);

  // Default last 7 days
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  // ✅ Decode payload from RAW JWT (not from "Bearer ...")
  const jwtPayload = useMemo(() => decodeJwtPayloadFromRawJwt(rawToken), [rawToken]);

  const defaultCampusFromToken =
    jwtPayload?.LoginCampusCode ??
    jwtPayload?.LoginCampusCOde ??
    jwtPayload?.loginCampusCode ??
    jwtPayload?.campusCode ??
    "";

  const [campusCode, setCampusCode] = useState(defaultCampusFromToken);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Keep campusCode in sync if token changes
  useEffect(() => {
    setCampusCode(defaultCampusFromToken || "");
  }, [defaultCampusFromToken]);

  const params = useMemo(() => {
    const s = startDate?.isValid?.() ? startDate.format("YYYY-MM-DD") : "";
    const e = endDate?.isValid?.() ? endDate.format("YYYY-MM-DD") : "";
    return {
      startDate: s,
      endDate: e,
      campusCode: campusCode || undefined,
    };
  }, [startDate, endDate, campusCode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    // ✅ Early guard (prevents spamming events with malformed tokens)
    if (!rawToken || rawToken.split(".").length !== 3) {
      setRows([]);
      setLoading(false);
      setError("Auth token is missing/invalid in browser storage. Please logout and login again.");
      return;
    }

    try {
      let url = "";
      if (tab === 0) url = "/api/audit-analytics/login-summary";
      if (tab === 1) url = "/api/audit-analytics/sessions";
      if (tab === 2) url = "/api/audit-analytics/report-views";
      if (tab === 3) url = "/api/audit-analytics/errors";

      const res = await axios.get(`${API_URL}${url}`, {
        params,
        headers: { Authorization: `Bearer ${rawToken}` }, // ✅ send clean token only
      });

      setRows(res.data.rows || []);
    } catch (err) {
      console.error("Audit analytics load failed", err);
      setRows([]);
      setError(err?.response?.data?.error || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, rawToken, params]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          <Box sx={headerSx}>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Audit & Usage Analytics
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Track logins, sessions, report views, and errors
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                onClick={() => {
                  setStartDate(dayjs().subtract(6, "day"));
                  setEndDate(dayjs());
                }}
                disabled={loading}
              >
                Reset (Last 7 Days)
              </Button>

              <Button variant="contained" sx={primaryBtnSx} onClick={load} disabled={loading}>
                {loading ? "Loading..." : "Apply"}
              </Button>
            </Box>
          </Box>

          <Box sx={bodySx}>
            {!!error && (
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{
                  borderRadius: 2,
                  border: "1px solid rgba(244,67,54,0.25)",
                  backgroundColor: "rgba(244,67,54,0.04)",
                  mb: 2,
                }}
              >
                {error}
              </Alert>
            )}

            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(v) => setStartDate(v)}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: { fullWidth: true, size: "small", sx: inputSx },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(v) => setEndDate(v)}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: { fullWidth: true, size: "small", sx: inputSx },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "right" }}>
                  <Button
                    variant="contained"
                    sx={{ ...primaryBtnSx, width: "100%" }}
                    onClick={load}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Apply"}
                  </Button>
                </Grid>
              </Grid>

              <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                sx={{
                  mt: 2,
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 900 },
                  "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
                }}
              >
                <Tab label="Login Activity" />
                <Tab label="Sessions" />
                <Tab label="Report Views" />
                <Tab label="Errors / Auth Failures" />
              </Tabs>
            </Paper>

            <Paper elevation={0} sx={tableWrapSx}>
              <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      {tab === 0 && (
                        <>
                          <TableCell>User ID</TableCell>
                          <TableCell>Login Count</TableCell>
                          <TableCell>Unique IPs</TableCell>
                          <TableCell>First Login</TableCell>
                          <TableCell>Last Login</TableCell>
                        </>
                      )}
                      {tab === 1 && (
                        <>
                          <TableCell>User ID</TableCell>
                          <TableCell>Sessions</TableCell>
                          <TableCell>Minutes Spent</TableCell>
                          <TableCell>Last Seen</TableCell>
                        </>
                      )}
                      {tab === 2 && (
                        <>
                          <TableCell>Feature</TableCell>
                          <TableCell>Views</TableCell>
                          <TableCell>Unique Users</TableCell>
                          <TableCell>Last View</TableCell>
                        </>
                      )}
                      {tab === 3 && (
                        <>
                          <TableCell>Action</TableCell>
                          <TableCell>Feature</TableCell>
                          <TableCell>Count</TableCell>
                          <TableCell>Last Time</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((r, idx) => (
                      <TableRow key={idx} sx={zebraRowSx(idx)}>
                        {tab === 0 && (
                          <>
                            <TableCell>{r.CampusUserID}</TableCell>
                            <TableCell>{r.login_count}</TableCell>
                            <TableCell>{r.unique_ips}</TableCell>
                            <TableCell>{r.first_login}</TableCell>
                            <TableCell>{r.last_login}</TableCell>
                          </>
                        )}
                        {tab === 1 && (
                          <>
                            <TableCell>{r.CampusUserID}</TableCell>
                            <TableCell>{r.sessions}</TableCell>
                            <TableCell>{r.minutes_spent}</TableCell>
                            <TableCell>{r.last_seen}</TableCell>
                          </>
                        )}
                        {tab === 2 && (
                          <>
                            <TableCell>{r.feature_key}</TableCell>
                            <TableCell>{r.views}</TableCell>
                            <TableCell>{r.unique_users}</TableCell>
                            <TableCell>{r.last_view}</TableCell>
                          </>
                        )}
                        {tab === 3 && (
                          <>
                            <TableCell>{r.action}</TableCell>
                            <TableCell>{r.feature_key}</TableCell>
                            <TableCell>{r.count}</TableCell>
                            <TableCell>{r.last_time}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}

                    {!loading && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 3, color: "text.secondary" }}>
                          No data for the selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
